import { useState } from "react";
import { Can } from "@core/permissions";
import { reportError } from "@portal/common/errorMessage";
import { CursorTable, type CursorTableColumn } from "@core/table";
import { tokens } from "@core/theme/tokens";
import type { User, UserInput } from "@core/types";
import {
  DialogHeader,
  EmptyState,
  ImpactConfirmDialog,
  inputStyle,
  ListLayout,
  Modal,
  RowIconAction,
  SecurityHeader,
  useToast,
} from "@portal/common";
import { UserForm } from "./UserForm";
import type {
  ResetPasswordResult,
  UserActiveFilter,
  UserCreateInput,
} from "./api";
import {
  useActivePlaces,
  useProfiles,
  useUserMutations,
  useUsers,
} from "./userQueries";
import { userStyles } from "./userStyles";

export const USERS_TABLE_PROTOTYPE_COLUMNS = {
  name: "1.5fr",
  username: "118px",
  email: "1.5fr",
  profile: "132px",
  place: "1.2fr",
  lastAccess: "148px",
  state: "104px",
  actions: "178px",
} as const;

export const USERS_TABLE_PROTOTYPE_MIN_WIDTH = 1260;
export const USERS_TABLE_1440_SCALE = 0.875;
export const USERS_FILTER_STYLES = {
  search: { ...inputStyle, width: userStyles.toolbar.searchWidth },
  profile: { ...inputStyle, width: "auto", minWidth: 160, cursor: "pointer" },
  state: { ...inputStyle, width: "auto", minWidth: 140, cursor: "pointer" },
};
export const USERS_RESET_PASSWORD_ACTION_LABEL = "Clave";

// Mensajes de SPEC-215 §7, verbatim: nunca el `title`/`detail` crudo del
// problem+json llega a la pantalla (SPEC-201 §4.4).
const ERROR_MESSAGES: Record<string, string> = {
  USERNAME_DUPLICATE: "Ya existe una cuenta con ese nombre de usuario",
  PLACE_REQUIRED:
    "Un perfil de validador requiere un comercio aliado o un taller asociado",
  PLACE_DUPLICATE: "Elige un comercio aliado o un taller, no ambos",
  NO_SELF_INACTIVATE: "No puedes inactivar tu propia cuenta",
  NO_SELF_DOWNGRADE: "No puedes cambiar tu propio perfil",
  LAST_ADMINISTRATOR: "Debe quedar al menos un administrador activo",
};
function placeLabel(user: User) {
  if (!user.place) return "—";
  const kind = user.place.type === "merchant" ? "Comercio" : "Taller";
  return `${user.place.name} · ${kind}`;
}
function formatLastAccess(value: string | null | undefined) {
  if (!value) return "Nunca";
  const date = new Date(value);
  const months = [
    "ene",
    "feb",
    "mar",
    "abr",
    "may",
    "jun",
    "jul",
    "ago",
    "sep",
    "oct",
    "nov",
    "dic",
  ];
  return `${String(date.getUTCDate()).padStart(2, "0")} ${months[date.getUTCMonth()]} ${date.getUTCFullYear()} · ${String(date.getUTCHours()).padStart(2, "0")}:${String(date.getUTCMinutes()).padStart(2, "0")}`;
}

export function UsersScreen() {
  const toast = useToast();
  const [search, setSearch] = useState("");
  const [profileFilter, setProfileFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState<UserActiveFilter>("all");
  const [editing, setEditing] = useState<User | null | undefined>(undefined);
  const [inactivate, setInactivate] = useState<User | null>(null);
  const [resettingPassword, setResettingPassword] = useState<User | null>(null);
  const [resetResult, setResetResult] = useState<
    (ResetPasswordResult & { name: string }) | null
  >(null);
  const [confirmationNotice, setConfirmationNotice] = useState<string | null>(
    null,
  );

  const profiles = useProfiles();
  const merchants = useActivePlaces("merchants");
  const workshops = useActivePlaces("workshops");
  const users = useUsers(search, profileFilter, activeFilter);
  const mutations = useUserMutations(search, profileFilter, activeFilter);

  const rows = users.data?.pages.flatMap((page) => page.items) ?? [];
  const lastPage = users.data?.pages.at(-1);

  const startCreate = () => {
    setConfirmationNotice(null);
    setEditing(null);
  };
  const startEdit = (user: User) => {
    setEditing(user);
  };
  const save = (input: UserCreateInput | UserInput) => {
    if (!editing) {
      mutations.create.mutate(input as UserCreateInput, {
        onSuccess: () => {
          setEditing(undefined);
          toast.show("Usuario creado correctamente.");
          setConfirmationNotice(
            "Usuario creado. Se envió un enlace de un solo uso al correo del usuario para establecer su contraseña.",
          );
        },
        onError: (error) => reportError(toast, error, ERROR_MESSAGES),
      });
      return;
    }
    mutations.edit.mutate(
      { id: editing.id, input: input as UserInput },
      {
        onSuccess: () => {
          setEditing(undefined);
          toast.show("Cambios guardados correctamente.");
        },
        onError: (error) => reportError(toast, error, ERROR_MESSAGES),
      },
    );
  };

  const confirmInactivation = () => {
    if (!inactivate) return;
    mutations.inactivate.mutate(inactivate.id, {
      onSuccess: () => {
        setInactivate(null);
        toast.show("Usuario inactivado correctamente.");
      },
      onError: (error) => {
        setInactivate(null);
        reportError(toast, error, ERROR_MESSAGES);
      },
    });
  };
  const reactivate = (user: User) => {
    mutations.reactivate.mutate(user.id, {
      onSuccess: () => toast.show("Usuario activado correctamente."),
      onError: (error) => reportError(toast, error, ERROR_MESSAGES),
    });
  };
  const confirmResetPassword = () => {
    if (!resettingPassword) return;
    const user = resettingPassword;
    mutations.resetPassword.mutate(user.id, {
      onSuccess: (result) => {
        setResetResult({ ...result, name: user.name });
        setResettingPassword(null);
        toast.show("Enlace de restablecimiento enviado.");
      },
      onError: (error) => reportError(toast, error, ERROR_MESSAGES),
    });
  };

  const columns: CursorTableColumn<User>[] = [
    {
      key: "name",
      header: "Nombre",
      width: USERS_TABLE_PROTOTYPE_COLUMNS.name,
      render: (u) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            minWidth: 0,
          }}
        >
          <span
            className="av"
            style={{ width: 30, height: 30, fontSize: 10, flex: "none" }}
          >
            {u.name
              .split(/\s+/)
              .map((part) => part[0])
              .slice(0, 2)
              .join("")}
          </span>
          <strong
            title={u.name}
            style={{
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {u.name}
          </strong>
        </div>
      ),
    },
    {
      key: "username",
      header: "Usuario",
      width: USERS_TABLE_PROTOTYPE_COLUMNS.username,
      render: (u) => (
        <span
          title={u.username}
          style={{
            display: "block",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {u.username}
        </span>
      ),
    },
    {
      key: "email",
      header: "Correo",
      width: USERS_TABLE_PROTOTYPE_COLUMNS.email,
      render: (u) => (
        <span
          title={u.email}
          style={{
            color: tokens.text.tertiary,
            display: "block",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {u.email}
        </span>
      ),
    },
    {
      key: "profile",
      header: "Perfil",
      width: USERS_TABLE_PROTOTYPE_COLUMNS.profile,
      render: (u) => (
        <span style={{ display: "inline-flex", minWidth: 132 }}>
          <span className="chip" style={{ padding: "6px 10px" }}>
            {u.profile.name}
          </span>
        </span>
      ),
    },
    {
      key: "place",
      header: "Comercio asociado",
      width: USERS_TABLE_PROTOTYPE_COLUMNS.place,
      render: (u) => (
        <span
          title={placeLabel(u)}
          style={{ display: "block", minWidth: 0, lineHeight: 1.35 }}
        >
          {placeLabel(u)}
        </span>
      ),
    },
    {
      key: "lastAccessAt",
      header: "Último acceso",
      width: USERS_TABLE_PROTOTYPE_COLUMNS.lastAccess,
      render: (u) => (
        <span
          title={formatLastAccess(u.lastAccessAt)}
          style={{
            color: tokens.text.tertiary,
            minWidth: 0,
            whiteSpace: "nowrap",
          }}
        >
          {formatLastAccess(u.lastAccessAt)}
        </span>
      ),
    },
    {
      key: "active",
      header: "Estado",
      width: USERS_TABLE_PROTOTYPE_COLUMNS.state,
      render: (u) => (
        <span className="chip" style={{ padding: "6px 10px" }}>
          {u.active ? "Activo" : "Inactivo"}
        </span>
      ),
    },
  ];

  // Las tres protecciones de SPEC-215 §5.1 se rechazan al intentar la acción,
  // nunca ocultando el botón: por eso "Inactivar" siempre se muestra para
  // quien tiene permiso, sin lógica local de "es mi propia cuenta" o "es el
  // último administrador".
  // Revisión en vivo 2026-09-22, punto #10 (barrido): tres acciones de texto
  // por fila ("Editar", "Clave", "Inactivar") en la columna de 178px del
  // prototipo. Sumadas con sus dos separaciones pasan de los 210px, así que
  // el grupo desbordaba la columna. Con ícono + tooltip mide 132px y entra
  // con aire. La columna se deja en los 178px del prototipo
  // (`USERS_TABLE_PROTOTYPE_COLUMNS`, fijada por prueba): las acciones están
  // alineadas a la derecha, así que el sobrante queda como separación con la
  // columna anterior y ninguna otra se mueve.
  const rowActions = (user: User) => (
    <>
      <Can module="security" action="canEdit">
        <RowIconAction
          label="Editar"
          icon="edit"
          onClick={() => startEdit(user)}
        />
        <RowIconAction
          label={USERS_RESET_PASSWORD_ACTION_LABEL}
          icon="lock"
          onClick={() => setResettingPassword(user)}
        />
      </Can>
      <Can module="security" action="canInactivate">
        {user.active ? (
          <RowIconAction
            label="Inactivar"
            icon="deactivate"
            onClick={() => setInactivate(user)}
          />
        ) : (
          <RowIconAction
            label="Activar"
            icon="check"
            onClick={() => reactivate(user)}
          />
        )}
      </Can>
    </>
  );

  const formReady =
    profiles.isSuccess && merchants.isSuccess && workshops.isSuccess;

  return (
    <div>
      <SecurityHeader />

      {editing !== undefined ? (
        formReady ? (
          <UserForm
            entry={editing ?? undefined}
            profiles={profiles.data ?? []}
            merchants={merchants.data ?? []}
            workshops={workshops.data ?? []}
            modal={{
              label: editing ? "Editar usuario" : "Nuevo usuario",
              width: userStyles.modal.width,
              onClose: () => setEditing(undefined),
            }}
            onSubmit={save}
            onCancel={() => setEditing(undefined)}
            submitting={mutations.create.isPending || mutations.edit.isPending}
          />
        ) : (
          // Revisión en vivo 2026-09-22, puntos #3/#4A/#15: el estado de
          // carga del mismo diálogo adopta las tres regiones para que el
          // encabezado no aparezca recién cuando terminan de cargar perfiles,
          // comercios y talleres — antes el diálogo era un panel con
          // "Cargando…" suelto y el título apareciendo después. Sin pie: no
          // hay ninguna acción que ofrecer mientras carga.
          <Modal
            label={editing ? "Editar usuario" : "Nuevo usuario"}
            width={userStyles.modal.width}
            onClose={() => setEditing(undefined)}
            // Punto #21: el mismo `DialogHeader` que muestra `UserForm` al
            // terminar de cargar, para que el encabezado no salte.
            header={
              <DialogHeader
                eyebrow={editing ? "Editar usuario" : "Nuevo usuario"}
                title={editing?.name || "Usuario sin nombre"}
              />
            }
          >
            {/* Sin `padding` propio: el `Modal` ya trae el de la región
                (`portalPrimitiveValues.modalRegions.bodyPadding`) y duplicarlo
                dejaba 48px — el mismo defecto del punto #15 en Círculo de
                miembros. */}
            Cargando…
          </Modal>
        )
      ) : (
        <ListLayout
          search={
            <>
              <input
                aria-label="Buscar por nombre, usuario o correo"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar por nombre, usuario o correo"
                style={USERS_FILTER_STYLES.search}
              />
              <select
                aria-label="Filtrar por perfil"
                value={profileFilter}
                onChange={(event) => setProfileFilter(event.target.value)}
                style={USERS_FILTER_STYLES.profile}
              >
                <option value="">Perfil · todos</option>
                {(profiles.data ?? []).map((profile) => (
                  <option key={profile.id} value={profile.id}>
                    {profile.name}
                  </option>
                ))}
              </select>
              <select
                aria-label="Filtrar por estado"
                value={activeFilter}
                onChange={(event) =>
                  setActiveFilter(event.target.value as UserActiveFilter)
                }
                style={USERS_FILTER_STYLES.state}
              >
                <option value="all">Estado · todos</option>
                <option value="active">Activo</option>
                <option value="inactive">Inactivo</option>
              </select>
              <div style={{ flex: 1 }} />
              <Can module="security" action="canCreate">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={startCreate}
                >
                  Nuevo usuario
                </button>
              </Can>
            </>
          }
        >
          {confirmationNotice ? (
            <p
              style={{
                color: tokens.text.secondary,
                background: tokens.surface.surface,
                border: `1px solid ${tokens.surface.line}`,
                borderRadius: tokens.radius.sm,
                padding: "10px 12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
              }}
            >
              <span>{confirmationNotice}</span>
              <button
                type="button"
                className="chip"
                aria-label="Cerrar aviso"
                onClick={() => setConfirmationNotice(null)}
              >
                ×
              </button>
            </p>
          ) : null}
          {users.isLoading ? (
            <div style={{ padding: 22 }}>Cargando usuarios…</div>
          ) : users.isError ? (
            <EmptyState kind="error" />
          ) : rows.length ? (
            <div style={{ overflow: "hidden" }}>
              <div
                style={{
                  minWidth: USERS_TABLE_PROTOTYPE_MIN_WIDTH,
                  zoom: USERS_TABLE_1440_SCALE,
                }}
              >
                <CursorTable
                  columns={columns}
                  rows={rows}
                  rowKey={(u) => u.id}
                  page={{
                    nextCursor: lastPage?.meta.next ?? null,
                    totalCount: lastPage?.meta.total ?? rows.length,
                  }}
                  onLoadMore={() => users.fetchNextPage()}
                  isLoadingMore={users.isFetchingNextPage}
                  rowActions={rowActions}
                  actionsHeader="Acciones"
                  actionWidth={USERS_TABLE_PROTOTYPE_COLUMNS.actions}
                />
              </div>
            </div>
          ) : (
            <EmptyState
              kind={search || profileFilter ? "no-results" : "no-data"}
            />
          )}
        </ListLayout>
      )}

      <ImpactConfirmDialog
        open={Boolean(inactivate)}
        eyebrow="Seguridad · Usuarios"
        title={`¿Inactivar a ${inactivate?.name ?? ""}?`}
        affectedCount={inactivate ? 1 : 0}
        affectedLabel="Su sesión se cierra y no podrá volver a ingresar. El historial de accesos y de validaciones se conserva."
        onCancel={() => setInactivate(null)}
        onConfirm={confirmInactivation}
        confirmLabel="Sí, inactivar"
      />
      <ImpactConfirmDialog
        open={Boolean(resettingPassword)}
        eyebrow="Seguridad · Usuarios"
        title="Restablecer la contraseña"
        affectedCount={resettingPassword ? 1 : 0}
        affectedLabel={
          resettingPassword
            ? `Se enviará un enlace de un solo uso a ${resettingPassword.email}. La contraseña actual deja de funcionar de inmediato.`
            : ""
        }
        onCancel={() => setResettingPassword(null)}
        onConfirm={confirmResetPassword}
        confirmLabel="Enviar enlace"
        danger={false}
        busy={mutations.resetPassword.isPending}
      />
      <ImpactConfirmDialog
        open={Boolean(resetResult)}
        eyebrow="Seguridad · Usuarios"
        title="Enlace enviado"
        affectedCount={resetResult ? 1 : 0}
        affectedLabel={
          resetResult
            ? `Se envió un enlace de un solo uso a ${resetResult.maskedEmail ?? "el correo del usuario"}${
                resetResult.validityHours
                  ? `, válido por ${resetResult.validityHours} horas`
                  : ""
              }.`
            : ""
        }
        onCancel={() => setResetResult(null)}
        onConfirm={() => setResetResult(null)}
        confirmLabel="Entendido"
        danger={false}
      />
    </div>
  );
}
